import { type Amount } from '../../../common/amount'
import { add, ensurePositivePrice, multiply, subtract } from '../../../utils/money'
import { type Quote } from '../../quotes'
import { PromoCodeType } from './promoCodes.enums'
import { type PromoCode } from './promoCodes.models'

/**
 * apply promocode to a given amount
 */
export const applyPromoCode = (amount: Amount, promoCode?: PromoCode): Amount => {
  if (!promoCode) {
    return { ...amount }
  }
  switch (promoCode.type) {
    case PromoCodeType.Amount:
      return ensurePositivePrice(subtract(amount, promoCode.value))
    case PromoCodeType.Percentage: {
      const rebate = multiply(amount, promoCode.value / 100)
      return ensurePositivePrice(subtract(amount, rebate))
    }
    default:
      return { ...amount }
  }
}

/**
 * this apply a promo code to a quote
 * fixed amount are applied to first job only, percentage are applied to all jobs
 */
export const applyPromoCodeToQuoteJob = (quote: Quote, jobIndex: number, promoCode?: PromoCode): Amount => {
  const job = quote.jobs[jobIndex]
  if (!job) {
    throw new Error('Invalid job index')
  }
  if (!promoCode) {
    return job.subtotal
  }
  switch (promoCode.type) {
    case PromoCodeType.Amount:
      return jobIndex === 0 ? applyPromoCode(job.subtotal, promoCode) : job.subtotal
    case PromoCodeType.Percentage:
      return applyPromoCode(job.subtotal, promoCode)
    default:
      return job.subtotal
  }
}

/**
 * this apply a promo code to a quote
 * fixed amount are applied to first job only, percentage are applied to all jobs
 */
export const applyPromoCodeToQuote = (quote: Quote, promoCode?: PromoCode): Amount => {
  if (!promoCode) {
    return quote.subtotal
  }
  const allJobsSubtotals = quote.jobs.map((job, index) => applyPromoCodeToQuoteJob(quote, index, promoCode))

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return add(allJobsSubtotals.shift()!, ...allJobsSubtotals)
}

/**
 * lazy function to calculate promo code on a given price
 */
export const useGetSubtotalWithPromoCode = () => {
  return (amount: Amount, promoCode?: PromoCode) => {
    if (!promoCode) {
      return amount
    }
    return applyPromoCode(amount, promoCode)
  }
}
